/**
 * Tile data fetching, and the queue that keeps a dashboard from flattening the
 * backend.
 *
 * **Why a queue.** The backend is one ECS task with a single shared DuckDB
 * connection (`config.py:get_connection()` hands out cursors off one instance),
 * running with a desired count of one. Eight tiles times six ships is 48
 * concurrent `POST /plot` that do not parallelize - they queue
 * inside DuckDB and compete for one 8 GB buffer pool. Firing them all at once
 * turns a dashboard load into a self-inflicted outage, so tiles take turns.
 *
 * Combined with viewport-lazy loading in `<dashboard-tile>`, a dashboard costs
 * roughly what is on screen rather than what it contains.
 */

import {
  fetchShipPlots,
  mergeBoxOverlay,
  mergeGaugeOverlay,
  mergeOverlay,
  mergeTableOverlay,
  supportsOverlay,
} from '../plot/multi-ship-plot.ts';
import { MAX_PLOT_SHIPS } from '../../state/store.ts';
import { buildShaping } from '../../state/request.ts';
import { shipLabel } from '../../utils/ships.ts';
import type { AnyPlotResponse } from '../../api/types.ts';
import type { ResolvedTile } from './tile-spec.ts';

/**
 * Tile requests in flight at once, across the whole dashboard.
 *
 * Three rather than one: the backend serializes the DuckDB work anyway, but a
 * little overlap hides the request round-trip and the response parse, and keeps
 * a slow tile from stalling the queue behind it completely.
 */
export const MAX_CONCURRENT_TILE_REQUESTS = 3;

let inFlight = 0;
const waiting: Array<() => void> = [];

/** Take a queue slot, run `task`, release the slot however it ends. */
async function queued<T>(task: () => Promise<T>): Promise<T> {
  if (inFlight >= MAX_CONCURRENT_TILE_REQUESTS) {
    await new Promise<void>((resolve) => waiting.push(resolve));
  }
  inFlight++;
  try {
    return await task();
  } finally {
    inFlight--;
    waiting.shift()?.();
  }
}

/** Queue depth, for the "waiting" hint in a tile that has not started yet. */
export function tileQueueDepth(): number {
  return waiting.length;
}

export interface TileResult {
  data: AnyPlotResponse;
  /** Non-null when several ships were fetched, for per-ship reporting. */
  warning?: string;
}

/**
 * Fetch one tile's data, waiting for a queue slot first.
 *
 * Multi-ship behavior deliberately differs from Explore's: a tile draws an
 * overlay when the plot type supports one, and otherwise draws the anchor ship
 * alone rather than splitting into panels. A dashboard tile is already small;
 * subdividing it into six heatmaps produces six unreadable ones, and the tile
 * says so instead.
 */
export async function fetchTile(resolved: ResolvedTile, refresh = false): Promise<TileResult> {
  const ships = resolved.ships.filter(Boolean).slice(0, MAX_PLOT_SHIPS);
  if (!ships.length) throw new Error('No ship selected');

  const overlayable = supportsOverlay(resolved.state.plot_type);
  const fetchShips = overlayable ? ships : ships.slice(0, 1);

  return queued(async () => {
    const results = await fetchShipPlots(fetchShips, {
      source: resolved.source,
      state: resolved.state,
      shaping: buildShaping(resolved.shaping),
      start: resolved.start,
      end: resolved.end,
    }, refresh);

    const notes: string[] = [];
    if (!overlayable && ships.length > 1) {
      notes.push(
        `${resolved.state.plot_type} tiles draw one ship - showing ${shipLabel(ships[0] ?? '')}.`,
      );
    }

    let data: AnyPlotResponse | null;
    if (overlayable && fetchShips.length > 1) {
      const pt = resolved.state.plot_type;
      data =
        pt === 'box'
          ? mergeBoxOverlay(results)
          : pt === 'table'
            ? mergeTableOverlay(results)
            : pt === 'gauge'
              ? mergeGaugeOverlay(results)
              : mergeOverlay(results);
    } else {
      data = results.find((r) => r.data)?.data ?? null;
    }
    if (!data) {
      // Every ship failed, or none returned rows. Prefer a real backend message
      // over a generic one - it usually names the column or the date range.
      throw new Error(results.find((r) => r.error)?.error ?? 'No data for this range.');
    }

    const failed = results.filter((r) => !r.data && r.error);
    if (failed.length && failed.length < results.length) {
      notes.push(`${failed.map((r) => shipLabel(r.ship)).join(', ')}: ${failed[0]!.error}`);
    }
    if (data.warning) notes.unshift(data.warning);

    const out: TileResult = { data };
    if (notes.length) out.warning = notes.join(' ');
    return out;
  });
}
